import { Injectable, inject } from '@angular/core';
import { TokenCredential } from '@azure/core-auth';
import { BlobServiceClient } from '@azure/storage-blob';
import { firstValueFrom } from 'rxjs';
import { AppConfig } from 'src/app/app-config.service';
import { ToolService } from 'src/app/features/tooling/services/tool.service';

export interface ImagePreview {
  key: string;
  name: string;
  reference: string;
  url: string;
  kind: 'new' | 'existing';
}

export interface ImageValidationResult {
  valid: boolean;
  error: string | null;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png'] as const;

@Injectable({ providedIn: 'root' })
export class BlobUploadService {
    private readonly toolService = inject(ToolService);
    private readonly defaultUploadContainer = 'tools-temp';

    generateBlobName(sessionKey: string, reference: string, folder: string): string {
        return `${sessionKey}/${folder}/${reference}`;
    }

    async uploadFile(
        file: File,
        options?: {
            blobName?: string;
            container?: string;
            accountUrl?: string;
        }
    ): Promise<string> {
        const blobName = options?.blobName ?? file.name;
        if (!blobName?.trim()) {
            throw new Error('blobName is required.');
        }

        const container = options?.container ?? this.defaultUploadContainer;
        const accountUrl = options?.accountUrl ?? AppConfig.settings?.blobUrl;
        if (!accountUrl) {
            throw new Error('Blob account URL is not configured (AppConfig.settings.blobUrl).');
        }

        // Fetch token from ToolService (same as AuthInterceptor)
        const response = await firstValueFrom(this.toolService.getImageStorageAccountTokenAsync());
        const token = response.accessToken;
        const expiration = Date.parse(response.expiration) || Date.now() + 50 * 60 * 1000;

        const credential: TokenCredential = {
            getToken: async () => ({ token, expiresOnTimestamp: expiration })
        };

        const blobServiceClient = new BlobServiceClient(accountUrl, credential);
        const containerClient = blobServiceClient.getContainerClient(container);
        const blockBlob = containerClient.getBlockBlobClient(blobName);

        await blockBlob.uploadData(file, {
            blobHTTPHeaders: {
                blobContentType: file.type || 'application/octet-stream',
            },
            concurrency: 4,
        });

        return blockBlob.url;
    }

    // Image-specific methods merged from ImageUploadService
    fileKey(file: File): string {
        return `${file.name}-${file.size}-${file.lastModified}`;
    }

    validateFile(file: File): ImageValidationResult {
        if (file.size > MAX_FILE_SIZE) {
            return {
                valid: false,
                error: `File "${file.name}" exceeds the maximum size of ${MAX_FILE_SIZE / 1024 / 1024}MB.`,
            };
        }
        if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
            return {
                valid: false,
                error: `File "${file.name}" has an unsupported type. Allowed types: JPEG, PNG.`,
            };
        }
        if (file.name.includes('/') || file.name.includes('\\')) {
            return {
                valid: false,
                error: `Invalid filename: "${file.name}". Please rename the file and try again.`,
            };
        }
        return { valid: true, error: null };
    }

    async uploadPreview(file: File, sessionKey: string, reference: string): Promise<void> {
        const blobName = this.generateBlobName(sessionKey, reference, 'preview');
        await this.uploadFile(file, { blobName });
    }

    async uploadGalleryImages(
        files: File[],
        sessionKey: string,
        referenceByKey: Map<string, string>
    ): Promise<{ failedKeys: Set<string>; failedNames: string[] }> {
        if (!files.length) {
            return { failedKeys: new Set(), failedNames: [] };
        }

        const uploads = files.map(file => {
            const key = this.fileKey(file);
            const reference = referenceByKey.get(key) ?? this.buildStampedReference(file.name);
            const blobName = this.generateBlobName(sessionKey, reference, 'gallery');
            return this.uploadFile(file, { blobName });
        });

        const results = await Promise.allSettled(uploads);

        const failedKeys = new Set(
            files
                .filter((_, i) => results[i].status === 'rejected')
                .map(file => this.fileKey(file))
        );

        const failedNames = files
            .filter(file => failedKeys.has(this.fileKey(file)))
            .map(file => file.name);

        return { failedKeys, failedNames };
    }

    buildStampedReference(originalFileName: string): string {
        const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, '');
        const fileName = String(originalFileName ?? '').trim();
        const delimeter = fileName.lastIndexOf('.');
        const extension = delimeter > 0 && delimeter < fileName.length - 1;

        const baseName = extension ? fileName.slice(0, delimeter) : fileName;
        const ext = extension ? fileName.slice(delimeter) : '';
        const sanitizedBase = baseName.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/^_+|_+$/g, '') || 'image';
        const sanitizedExt = ext.replace(/[^a-zA-Z0-9.]/g, '').toLowerCase();

        return `${sanitizedBase}_${timestamp}${sanitizedExt}`;
    }

    deduplicateFiles(existingFiles: File[], newFiles: File[]): File[] {
        const existing = new Map(existingFiles.map(f => [this.fileKey(f), f]));
        const toAdd: File[] = [];

        for (const file of newFiles) {
            const key = this.fileKey(file);
            if (!existing.has(key)) {
                existing.set(key, file);
                toAdd.push(file);
            }
        }

        return toAdd;
    }
}